import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { SideBarService } from './services/side-bar.service';
import { FontSizeControlsContainerComponent } from '../font-size-controls-container/font-size-controls-container.component';
import { SideBarMenuItemGroup } from 'src/app/models/side-bar-menu-item-group';
import { SideBarNavigationComponent } from 'src/app/components/side-bar/side-bar-navigation/side-bar-navigation.component';
import {
  maxSidebarWidthRem,
  minSidebarWidthRem,
  pxToRem,
  sidebarCollapseDelayMs,
  sidebarExpandDelayMs,
  sidebarKeyboardLargeStepRem,
  sidebarKeyboardStepRem,
  sidebarUnpinCollapseDelayMs
} from 'src/app/components/side-bar/consts/sidebar-width.const';
import { IconButtonDirective, IconComponent, ButtonDirective } from '@bioreferencehealth/ui-kit';

@Component({
  selector: 'app-side-bar',
  standalone: true,
  imports: [FontSizeControlsContainerComponent, SideBarNavigationComponent, AsyncPipe, IconComponent, IconButtonDirective, ButtonDirective],
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.scss'],
  host: {
    '[class.side-bar--resizing]': 'isResizing()',
    '[class.side-bar--auto-collapsed]': 'isAutoCollapsed()',
    '[style.width.rem]': 'isAutoCollapsed() ? minSidebarWidth : sidebarWidth()'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SideBarComponent implements OnInit, OnDestroy {
  public readonly service: SideBarService = inject(SideBarService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly router: Router = inject(Router);

  public readonly menuItemGroups$: Observable<SideBarMenuItemGroup[]> = this.service.menuItemGroups$;
  public readonly isSettingsLoading$: Observable<boolean> = this.service.isSettingsLoading$;
  public readonly isAdminMode$: Observable<boolean> = this.service.isAdminMode$;

  public readonly isResizing = signal(false);
  public readonly isAutoCollapsed = signal(true);

  public readonly sidebarWidth = input(minSidebarWidthRem);
  public readonly isPinned = input(false);
  public readonly isResizable = input(true);
  public readonly sidebarWidthChange = output<number>();
  public readonly sidebarAutoCollapsedChange = output<boolean>();
  public readonly sidebarPinnedChange = output<boolean>();

  protected readonly minSidebarWidth = minSidebarWidthRem;
  protected readonly maxSidebarWidth = maxSidebarWidthRem;

  private resizeStartX = 0;
  private resizeStartWidth = minSidebarWidthRem;
  private isHovered = false;
  private isMousePressed = false;
  private hasFocusWithin = false;
  private isCollapsedByUnpin = false;
  private isIgnoringMouseEnterAfterCollapse = false;
  private previousPinnedState: boolean | null = null;
  private autoCollapseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const isPinned = this.isPinned();
      const wasJustUnpinned = this.previousPinnedState === true && !isPinned;
      this.previousPinnedState = isPinned;

      if (isPinned) {
        this.scheduleAutoCollapseStateUpdate(true);
      } else if (wasJustUnpinned) {
        this.isCollapsedByUnpin = true;
        this.hasFocusWithin = false;
        const delay = this.isHovered ? 0 : sidebarUnpinCollapseDelayMs;
        this.clearAutoCollapseTimer();
        if (delay === 0) {
          this.applyCollapseIgnoringTransientMouseEvents();
        } else {
          this.autoCollapseTimer = setTimeout(() => {
            this.autoCollapseTimer = null;
            this.applyCollapseIgnoringTransientMouseEvents();
          }, delay);
        }
      }
    });

    effect(() => {
      if (!this.isResizable() && this.isResizing()) {
        this.isResizing.set(false);
      }
    });
  }

  public ngOnInit(): void {
    this.scheduleAutoCollapseStateUpdate(true);
  }

  public ngOnDestroy(): void {
    this.clearAutoCollapseTimer();
  }

  public onPinToggle(): void {
    this.sidebarPinnedChange.emit(!this.isPinned());
  }

  public onExitAdmin(): void {
    this.router.navigateByUrl(this.service.getReturnUrl());
  }

  public onResizeStart(event: MouseEvent): void {
    if (!this.isResizable()) {
      return;
    }

    event.preventDefault();
    this.isResizing.set(true);
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.sidebarWidth();
    this.scheduleAutoCollapseStateUpdate();
  }

  @HostListener('mousedown')
  public onSidebarMouseDown(): void {
    this.isMousePressed = true;
  }

  @HostListener('mouseenter')
  public onSidebarMouseEnter(): void {
    if (this.isIgnoringMouseEnterAfterCollapse) {
      return;
    }
    this.isHovered = true;
    this.scheduleAutoCollapseStateUpdate();
  }

  @HostListener('mouseleave')
  public onSidebarMouseLeave(): void {
    if (this.isIgnoringMouseEnterAfterCollapse) {
      return;
    }
    this.isHovered = false;
    this.isCollapsedByUnpin = false;
    this.scheduleAutoCollapseStateUpdate();
  }

  @HostListener('focusin')
  public onSidebarFocusIn(): void {
    if (this.isMousePressed) {
      return;
    }
    this.hasFocusWithin = true;
    this.scheduleAutoCollapseStateUpdate();
  }

  @HostListener('focusout', ['$event'])
  public onSidebarFocusOut(event: FocusEvent): void {
    const nextFocusedElement = event.relatedTarget;

    if (nextFocusedElement && this.elementRef.nativeElement.contains(nextFocusedElement as Node)) {
      return;
    }

    this.hasFocusWithin = false;
    this.scheduleAutoCollapseStateUpdate();
  }

  public onResizeHandleKeydown(event: KeyboardEvent): void {
    if (!this.isResizable()) {
      return;
    }

    let nextWidth = this.sidebarWidth();
    const step = event.shiftKey ? sidebarKeyboardLargeStepRem : sidebarKeyboardStepRem;

    switch (event.key) {
      case 'ArrowRight':
        nextWidth = this.sidebarWidth() + step;
        break;
      case 'ArrowLeft':
        nextWidth = this.sidebarWidth() - step;
        break;
      case 'Home':
        nextWidth = this.minSidebarWidth;
        break;
      case 'End':
        nextWidth = this.maxSidebarWidth;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.emitSidebarWidth(nextWidth);
  }

  @HostListener('document:mousemove', ['$event'])
  public onDocumentMouseMove(event: MouseEvent): void {
    if (!this.isResizable() || !this.isResizing()) {
      return;
    }

    const widthDiffPx = event.clientX - this.resizeStartX;
    this.emitSidebarWidth(this.resizeStartWidth + pxToRem(widthDiffPx));
  }

  @HostListener('document:mouseup')
  public onDocumentMouseUp(): void {
    this.isMousePressed = false;
    this.isResizing.set(false);
    this.scheduleAutoCollapseStateUpdate();
  }

  private emitSidebarWidth(width: number): void {
    this.sidebarWidthChange.emit(this.clampSidebarWidth(width));
  }

  private clampSidebarWidth(width: number): number {
    return Math.max(this.minSidebarWidth, Math.min(this.maxSidebarWidth, width));
  }

  // Collapses the sidebar and briefly ignores transient mouse events that the
  // browser fires when the sidebar element shrinks past the cursor position.
  // After the ignore window closes, hover state is reset to a clean baseline so
  // any events suppressed during the window do not leave stale isHovered /
  // isCollapsedByUnpin values.
  private applyCollapseIgnoringTransientMouseEvents(): void {
    this.isIgnoringMouseEnterAfterCollapse = true;
    this.applyAutoCollapseState(true);
    setTimeout(() => {
      this.isIgnoringMouseEnterAfterCollapse = false;
      this.isHovered = false;
      this.isCollapsedByUnpin = false;
    }, 0);
  }

  private scheduleAutoCollapseStateUpdate(applyImmediately = false): void {
    const effectivelyHovered = this.isHovered && !this.isCollapsedByUnpin;
    const nextAutoCollapsed = this.isPinned() ? false : !effectivelyHovered && !this.hasFocusWithin && !this.isResizing();
    if (applyImmediately) {
      this.clearAutoCollapseTimer();
      this.applyAutoCollapseState(nextAutoCollapsed);
      return;
    }

    const delay = nextAutoCollapsed ? sidebarCollapseDelayMs : sidebarExpandDelayMs;

    this.clearAutoCollapseTimer();
    this.autoCollapseTimer = setTimeout(() => {
      this.autoCollapseTimer = null;
      this.applyAutoCollapseState(nextAutoCollapsed);
    }, delay);
  }

  private applyAutoCollapseState(nextAutoCollapsed: boolean): void {
    if (this.isAutoCollapsed() === nextAutoCollapsed) {
      return;
    }

    this.isAutoCollapsed.set(nextAutoCollapsed);
    this.sidebarAutoCollapsedChange.emit(nextAutoCollapsed);
  }

  private clearAutoCollapseTimer(): void {
    if (this.autoCollapseTimer === null) {
      return;
    }

    clearTimeout(this.autoCollapseTimer);
    this.autoCollapseTimer = null;
  }
}
